import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { Capacitor } from '@capacitor/core';
import { buildApiUrl, authFetch } from '@/config/api';
import { useAuth } from './AuthContext';
import type { Workspace, WorkspaceMembership, WorkspaceRole } from '@/types/workspace';
import { authEvents, AUTH_EVENTS } from '@/lib/authEvents';
import { beginNativeStartupTrace, markNativeStartup } from '@/lib/nativeStartupTrace';

export interface JoinWorkspaceResult {
  success: boolean;
  workspaceId?: string;
  message: string;
  status: 'joined' | 'request_pending' | 'already_member' | 'domain_mismatch' | 'invite_required' | 'request_failed' | 'error';
}

interface WorkspaceContextType {
  workspaces: WorkspaceMembership[];
  currentWorkspace: Workspace | null;
  currentWorkspaceId: string | null;
  currentRole: WorkspaceRole | null;
  isWorkspaceAdmin: boolean;
  isSupportView: boolean;
  isLoadingWorkspaces: boolean;
  workspaceError: string | null;
  switchWorkspace: (workspaceId: string) => void;
  refreshWorkspaces: (switchToWorkspaceId?: string, options?: { background?: boolean }) => Promise<void>;
  leaveWorkspace: (workspaceId: string) => Promise<void>;
  joinWorkspace: (workspaceId: string) => Promise<JoinWorkspaceResult>;
  hasMultipleWorkspaces: boolean;
}

const WorkspaceContext = createContext<WorkspaceContextType | undefined>(undefined);

const WORKSPACE_STORAGE_KEY = 'currentWorkspaceId';
const WORKSPACE_FETCH_RETRY_DELAYS_MS = [150, 350] as const;
const WORKSPACE_FETCH_TIMEOUT_MS = Capacitor.isNativePlatform() ? 30000 : 12000;
const WORKSPACE_AUTO_RETRY_DELAY_MS = 5000;

function getStoredWorkspaceId() {
  try {
    return localStorage.getItem(WORKSPACE_STORAGE_KEY);
  } catch {
    return null;
  }
}

const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

function withTimeout<T>(promise: Promise<T>, timeoutMs: number, timeoutMessage: string): Promise<T> {
  return new Promise((resolve, reject) => {
    const timeoutId = window.setTimeout(() => {
      reject(new Error(timeoutMessage));
    }, timeoutMs);

    promise
      .then((value) => {
        window.clearTimeout(timeoutId);
        resolve(value);
      })
      .catch((error) => {
        window.clearTimeout(timeoutId);
        reject(error);
      });
  });
}

export function WorkspaceProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, user, isAdmin } = useAuth();
  const [workspaces, setWorkspaces] = useState<WorkspaceMembership[]>([]);
  const [currentWorkspaceId, setCurrentWorkspaceId] = useState<string | null>(() => getStoredWorkspaceId());
  const [isLoadingWorkspaces, setIsLoadingWorkspaces] = useState(() => !getStoredWorkspaceId());
  const [workspaceError, setWorkspaceError] = useState<string | null>(null);

  useEffect(() => {
    beginNativeStartupTrace('workspace_provider_mounted', {
      hasStoredWorkspaceId: !!currentWorkspaceId,
    });
  }, []);

  // Get current workspace object and role
  const currentMembership = workspaces.find(w => w.workspaceId === currentWorkspaceId);
  const currentWorkspace = currentMembership?.workspace || null;
  const currentRole = currentMembership?.role || null;
  const isWorkspaceAdmin = currentRole === 'Admin' || currentRole === 'Owner';
  const isSupportView = !!(isAdmin && currentWorkspaceId && !currentMembership);

  // Load workspaces when authenticated
  const refreshWorkspaces = useCallback(async (switchToWorkspaceId?: string, options?: { background?: boolean }) => {
    const isBackgroundRefresh = options?.background === true;
    const storedId = getStoredWorkspaceId();

    if (!isAuthenticated) {
      setWorkspaces([]);
      setIsLoadingWorkspaces(false);
      setWorkspaceError(null);
      return;
    }

    if (!isBackgroundRefresh) {
      setIsLoadingWorkspaces(true);
      setWorkspaceError(null);
    }

    markNativeStartup('workspace_refresh_started', {
      background: isBackgroundRefresh,
      hasStoredWorkspaceId: !!storedId,
    });

    try {
      let response: Response | null = null;
      for (let attempt = 0; attempt <= WORKSPACE_FETCH_RETRY_DELAYS_MS.length; attempt += 1) {
        try {
          response = await withTimeout(
            authFetch(buildApiUrl('/api/workspaces/my')),
            WORKSPACE_FETCH_TIMEOUT_MS,
            '[WorkspaceContext] /api/workspaces/my timed out'
          );

          if (response.ok || response.status < 500) {
            break;
          }

          if (attempt < WORKSPACE_FETCH_RETRY_DELAYS_MS.length) {
            await delay(WORKSPACE_FETCH_RETRY_DELAYS_MS[attempt]);
            continue;
          }

          break;
        } catch {
          if (attempt < WORKSPACE_FETCH_RETRY_DELAYS_MS.length) {
            await delay(WORKSPACE_FETCH_RETRY_DELAYS_MS[attempt]);
            continue;
          }

          throw new Error('[WorkspaceContext] /api/workspaces/my timed out');
        }
      }

      if (!response) {
        throw new Error('Failed to load workspaces');
      }

      if (!response.ok) {
        // Check if it's a server error (5xx) vs client error (4xx)
        if (response.status >= 500) {
          throw new Error('Server is starting up or temporarily unavailable. Please wait a moment and refresh.');
        }
        throw new Error(`Failed to fetch workspaces: ${response.status}`);
      }
      const membershipData: WorkspaceMembership[] = await response.json();
      setWorkspaces(membershipData);
      setWorkspaceError(null);
      markNativeStartup('workspace_refresh_succeeded', {
        background: isBackgroundRefresh,
        count: membershipData.length,
      });

      if (membershipData.length === 0) {
        if (storedId && storedId === localStorage.getItem('workspaceId') && isAdmin) {
          return;
        }

        setCurrentWorkspaceId(null);
        localStorage.removeItem(WORKSPACE_STORAGE_KEY);
        localStorage.removeItem('workspaceId');
        return;
      }

      // If a specific workspace ID was requested (e.g., after accepting an invitation), switch to it
      if (switchToWorkspaceId) {
        const requestedWorkspace = membershipData.find(w => w.workspaceId === switchToWorkspaceId);
        if (requestedWorkspace) {
          setCurrentWorkspaceId(switchToWorkspaceId);
          localStorage.setItem(WORKSPACE_STORAGE_KEY, switchToWorkspaceId);
          localStorage.setItem('workspaceId', switchToWorkspaceId);
          markNativeStartup('workspace_selected', { workspaceId: switchToWorkspaceId });
          return;
        }
      }

      // If no current workspace is set, or current workspace is not in the list, select the first one
      // UNLESS the stored workspace is being accessed via admin privileges (not in member list)
      const validWorkspace = membershipData.find(w => w.workspaceId === storedId);
      
      if (!validWorkspace && membershipData.length > 0) {
        // Check if we have a stored workspace ID that might be admin-accessed
        if (storedId && storedId === localStorage.getItem('workspaceId')) {
          // Keep the admin-accessed workspace, don't switch away from it
          // The backend will validate admin access via RLS
          console.log('[WorkspaceContext] Preserving admin-accessed workspace:', storedId);
          markNativeStartup('workspace_selected', { workspaceId: storedId, mode: 'preserved' });
        } else {
          // No stored workspace or stored workspace changed - switch to first member workspace
          const firstWorkspaceId = membershipData[0].workspaceId;
          setCurrentWorkspaceId(firstWorkspaceId);
          localStorage.setItem(WORKSPACE_STORAGE_KEY, firstWorkspaceId);
          // Keep the legacy key in sync for authFetch() workspace header.
          localStorage.setItem('workspaceId', firstWorkspaceId);
          markNativeStartup('workspace_selected', { workspaceId: firstWorkspaceId, mode: 'defaulted' });
        }
      } else if (validWorkspace) {
        setCurrentWorkspaceId(storedId);
        // Keep the legacy key in sync for authFetch() workspace header.
        localStorage.setItem('workspaceId', storedId!);
        markNativeStartup('workspace_selected', { workspaceId: storedId!, mode: 'restored' });
      }
    } catch (error) {
      console.error('Failed to load workspaces:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to load workspaces';
      setWorkspaceError(errorMessage);

      const preserveBootstrappedState = isBackgroundRefresh && !!storedId;
      if (!preserveBootstrappedState) {
        setWorkspaces([]);
        setCurrentWorkspaceId(null);
      }

      markNativeStartup('workspace_refresh_failed', {
        background: isBackgroundRefresh,
        error: errorMessage,
      });
    } finally {
      if (!isBackgroundRefresh) {
        setIsLoadingWorkspaces(false);
      }
    }
  // Only depend on isAuthenticated - currentWorkspaceId changes should not retrigger fetch
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, isAdmin]);

  // Load workspaces on mount and when auth changes
  useEffect(() => {
    const hasBootstrappedWorkspace = !!getStoredWorkspaceId();
    void refreshWorkspaces(undefined, hasBootstrappedWorkspace ? { background: true } : undefined);
  }, [refreshWorkspaces, user?.userId]);

  useEffect(() => {
    if (!currentWorkspaceId) return;

    localStorage.setItem('workspaceId', currentWorkspaceId);
  }, [currentWorkspaceId]);

  // Refresh workspaces when another part of the app changes workspace context
  // (e.g., after accepting an invitation during auth callback).
  useEffect(() => {
    const onWorkspaceContextChanged = (payload?: { workspaceId?: string }) => {
      if (payload?.workspaceId) {
        setCurrentWorkspaceId(payload.workspaceId);
        localStorage.setItem(WORKSPACE_STORAGE_KEY, payload.workspaceId);
        localStorage.setItem('workspaceId', payload.workspaceId);
      }

      void refreshWorkspaces(payload?.workspaceId);
    };

    authEvents.on(AUTH_EVENTS.WORKSPACE_CONTEXT_CHANGED, onWorkspaceContextChanged);
    return () => {
      authEvents.off(AUTH_EVENTS.WORKSPACE_CONTEXT_CHANGED, onWorkspaceContextChanged);
    };
  }, [refreshWorkspaces]);

  // Native startup and deep-link returns can transiently stall network requests.
  // Keep retrying in the background after timeout errors so the app self-recovers
  // without requiring manual logout/login.
  useEffect(() => {
    if (!isAuthenticated) return;
    if (isLoadingWorkspaces) return;
    if (!workspaceError?.includes('timed out')) return;

    const retryTimer = window.setTimeout(() => {
      void refreshWorkspaces(undefined, { background: true });
    }, WORKSPACE_AUTO_RETRY_DELAY_MS);

    return () => {
      window.clearTimeout(retryTimer);
    };
  }, [isAuthenticated, isLoadingWorkspaces, workspaceError, refreshWorkspaces]);

  // Switch workspace
  const switchWorkspace = useCallback((workspaceId: string) => {
    // Allow switching even if not in workspaces list (for admin access)
    // The workspace will be validated by the backend middleware
    setCurrentWorkspaceId(workspaceId);
    localStorage.setItem(WORKSPACE_STORAGE_KEY, workspaceId);
    
    // Also update the old workspaceId key for backward compatibility
    localStorage.setItem('workspaceId', workspaceId);
    markNativeStartup('workspace_selected', { workspaceId, mode: 'manual' });
  }, []);

  // Leave workspace
  const leaveWorkspace = useCallback(async (workspaceId: string) => {
    const response = await authFetch(buildApiUrl(`/api/workspaces/${workspaceId}/leave`), {
      method: 'POST',
    });

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.message || 'Failed to leave workspace');
    }

    // Remove from local state
    const remainingWorkspaces = workspaces.filter(w => w.workspaceId !== workspaceId);
    setWorkspaces(remainingWorkspaces);

    // If we left the current workspace, switch to another one
    if (currentWorkspaceId === workspaceId) {
      if (remainingWorkspaces.length > 0) {
        const newWorkspaceId = remainingWorkspaces[0].workspaceId;
        setCurrentWorkspaceId(newWorkspaceId);
        localStorage.setItem(WORKSPACE_STORAGE_KEY, newWorkspaceId);
        localStorage.setItem('workspaceId', newWorkspaceId);
      } else {
        setCurrentWorkspaceId(null);
        localStorage.removeItem(WORKSPACE_STORAGE_KEY);
        localStorage.removeItem('workspaceId');
      }
    }
  }, [workspaces, currentWorkspaceId]);

  // Join workspace
  const joinWorkspace = useCallback(async (workspaceId: string): Promise<JoinWorkspaceResult> => {
    const response = await authFetch(buildApiUrl(`/api/workspaces/${workspaceId}/join`), {
      method: 'POST',
    });

    const data = await response.json();
    
    if (!response.ok) {
      return {
        success: false,
        message: data.message || 'Failed to join workspace',
        status: data.status || 'error',
      };
    }

    // If successfully joined, refresh workspaces and switch to the new one
    if (data.status === 'joined' || data.status === 'already_member') {
      await refreshWorkspaces(workspaceId);
    }

    return data as JoinWorkspaceResult;
  }, [refreshWorkspaces]);

  // Clear workspace on logout
  useEffect(() => {
    if (!isAuthenticated) {
      setWorkspaces([]);
      setCurrentWorkspaceId(null);
      localStorage.removeItem(WORKSPACE_STORAGE_KEY);
      localStorage.removeItem('workspaceId');
    }
  }, [isAuthenticated]);

  const value: WorkspaceContextType = {
    workspaces,
    currentWorkspace,
    currentWorkspaceId,
    currentRole,
    isWorkspaceAdmin,
    isSupportView,
    isLoadingWorkspaces,
    workspaceError,
    switchWorkspace,
    refreshWorkspaces,
    leaveWorkspace,
    joinWorkspace,
    hasMultipleWorkspaces: workspaces.length > 1,
  };

  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
}

export function useWorkspace() {
  const context = useContext(WorkspaceContext);
  if (context === undefined) {
    throw new Error('useWorkspace must be used within a WorkspaceProvider');
  }
  return context;
}
